import { useState, useEffect } from 'react';
import RegistrationModal from './components/RegistrationModal';
import { useSEO } from '../../utils/seo';

export default function Home() {
  const [selectedPackage, setSelectedPackage] = useState<'single' | 'double'>('double');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalOccupancy, setModalOccupancy] = useState<'single' | 'double'>('double');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [registrantInfo, setRegistrantInfo] = useState({ name: '', email: '' });

  // Check for registration success in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const registrationStatus = urlParams.get('registration');
    
    if (registrationStatus === 'success') {
      const name = urlParams.get('name') || '';
      const email = urlParams.get('email') || '';
      const occupancy = urlParams.get('occupancy') || 'double';
      
      setRegistrantInfo({ name, email });
      setShowSuccessMessage(true);
      
      // Send registration emails after successful payment
      const sendEmails = async () => {
        try {
          const registrationData = JSON.parse(localStorage.getItem('pendingRegistration') || '{}');
          
          if (registrationData.email) {
            const response = await fetch(
              `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-registration-emails`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  email: registrationData.email,
                  firstName: registrationData.firstName,
                  lastName: registrationData.lastName,
                  occupancy: occupancy,
                  registrationData: registrationData,
                }),
              }
            );

            if (response.ok) {
              console.log('✅ Registration emails sent successfully');
              // Clear the pending registration data
              localStorage.removeItem('pendingRegistration');
            } else {
              console.error('❌ Failed to send registration emails');
            }
          }
        } catch (error) {
          console.error('❌ Error sending registration emails:', error);
        }
      };

      sendEmails();
      
      // Clean up URL
      window.history.replaceState({}, '', '/');
    }
  }, []);

  // SEO Configuration with Schema.org
  useSEO({
    title: 'Casa de Campo Golf Tournament 2026 | Bucket List Golf Series Dominican Republic',
    description: 'Join Bucket List Golf\'s Escape to Paradise 2026 at Casa de Campo, Dominican Republic. Play legendary Teeth of the Dog, Dye Fore, and The Links courses. March 12-16, 2026. All-inclusive luxury golf vacation with championship tournament, founding member benefits, and unforgettable Caribbean golf experience at Pete Dye\'s masterpiece resort.',
    keywords: 'Casa de Campo golf tournament, Dominican Republic golf vacation, Teeth of the Dog golf course, Pete Dye courses Caribbean, luxury golf resort 2026, Bucket List Golf Series, La Romana golf events',
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://casaapril2026.bucketlistgolf.org'}/`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'SportsEvent',
      name: 'Escape to Paradise 2026 - Casa de Campo Golf Tournament',
      description: 'Join Bucket List Golf\'s Escape to Paradise 2026 at Casa de Campo, Dominican Republic. Play legendary Teeth of the Dog, Dye Fore, and The Links courses. March 12-16, 2026.',
      startDate: '2026-03-12',
      endDate: '2026-03-16',
      location: {
        '@type': 'Place',
        name: 'Casa de Campo Resort',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'La Romana',
          addressRegion: 'La Romana Province',
          addressCountry: 'DO',
          postalCode: '22000'
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: '18.4208',
          longitude: '-68.9378'
        }
      },
      organizer: {
        '@type': 'Organization',
        name: 'Bucket List Golf Series',
        url: import.meta.env.VITE_SITE_URL || 'https://casaapril2026.bucketlistgolf.org',
        email: 'Info@BucketListGolf.org',
        telephone: '+1-413-222-7701',
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '+1-413-222-7701',
          contactType: 'Customer Service',
          email: 'Info@BucketListGolf.org',
          availableLanguage: 'English'
        }
      },
      offers: [
        {
          '@type': 'Offer',
          name: 'Golfer - Double Occupancy',
          price: '3299',
          priceCurrency: 'USD',
          availability: 'https://schema.org/LimitedAvailability',
          validFrom: '2024-01-01',
          url: import.meta.env.VITE_SITE_URL || 'https://casaapril2026.bucketlistgolf.org'
        },
        {
          '@type': 'Offer',
          name: 'Golfer - Single Occupancy',
          price: '3959',
          priceCurrency: 'USD',
          availability: 'https://schema.org/LimitedAvailability',
          validFrom: '2024-01-01',
          url: import.meta.env.VITE_SITE_URL || 'https://casaapril2026.bucketlistgolf.org'
        }
      ],
      performer: {
        '@type': 'SportsTeam',
        name: 'Tournament Participants'
      },
      eventStatus: 'https://schema.org/EventScheduled',
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      image: 'https://static.readdy.ai/image/57c982531ce74d8992a8785896e0bafd/7072dc4b0739e0f36063f66feba57f4b.webp'
    }
  });

  const handleRegistration = (occupancy: 'single' | 'double') => {
    setModalOccupancy(occupancy);
    setIsModalOpen(true);
  };

  const scrollToRegistration = () => {
    const registrationSection = document.getElementById('registration-section');
    if (registrationSection) {
      registrationSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Success Message Banner */}
      {showSuccessMessage && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-emerald-600 text-white py-6 px-6 shadow-lg animate-slide-down">
          <div className="max-w-7xl mx-auto flex items-start justify-between gap-4">
            <div className="flex items-start gap-4 flex-1">
              <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <i className="ri-checkbox-circle-fill text-4xl"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold mb-2">Registration Successful! 🎉</h3>
                <p className="text-white/95 text-lg leading-relaxed">
                  {registrantInfo.name && `Welcome ${registrantInfo.name}! `}
                  Your payment has been received and your spot is secured for Escape to Paradise 2026!
                </p>
                <p className="text-white/95 text-lg leading-relaxed mt-2">
                  We've sent a confirmation email to <strong>{registrantInfo.email || 'your email'}</strong> with all the event details.
                  Chris will be in touch shortly with next steps and additional information about your Casa de Campo golf adventure!
                </p>
                <p className="text-white/90 text-base mt-3 italic">
                  Check your inbox (and spam folder) for your welcome email. See you in paradise! ⛳🏝️
                </p>
              </div>
            </div>
            <button 
              onClick={() => setShowSuccessMessage(false)}
              className="text-white/80 hover:text-white transition-colors cursor-pointer flex-shrink-0 w-10 h-10 flex items-center justify-center"
              aria-label="Close notification"
            >
              <i className="ri-close-line text-3xl"></i>
            </button>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/57c982531ce74d8992a8785896e0bafd/7072dc4b0739e0f36063f66feba57f4b.webp" 
            alt="Teeth of the Dog Golf Course" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        </div>
        
        <div className="relative z-10 text-center text-white px-6 max-w-5xl mx-auto">
          <div className="inline-block bg-red-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-6 uppercase tracking-wider">
            LIMITED SPOTS AVAILABLE - REGISTER EARLY
          </div>
          <p className="text-xl md:text-2xl mb-4 text-white/90 font-medium">
            Bucket List Series Presents:
          </p>
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Escape to Paradise 2026
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-white/90">
            Casa de Campo, Dominican Republic | March 12-16th, 2026
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={scrollToRegistration}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
            >
              Register Now
            </button>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-white text-3xl"></i>
        </div>
      </section>

      {/* Event Overview */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">An Unforgettable Golf Experience</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Join us for five days of championship golf on Pete Dye's legendary courses at one of the Caribbean's most exclusive resorts
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-calendar-line text-3xl text-emerald-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Event Dates</h3>
              <p className="text-gray-600 text-lg">
                March 12th-16th, 2026<br />
                Four nights, three days of golf
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-map-pin-line text-3xl text-emerald-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Location</h3>
              <p className="text-gray-600 text-lg">
                Casa de Campo Resort<br />
                La Romana, Dominican Republic
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-golf-ball-line text-3xl text-emerald-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Golf Rounds</h3>
              <p className="text-gray-600 text-lg">
                3 championship rounds<br />
                Plus complimentary replays
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Golf Courses Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Championship Golf on Three Pete Dye Masterpieces</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Your golf package includes rounds on three world-class courses, each offering a unique and unforgettable experience
            </p>
          </div>

          <div className="space-y-8">
            {/* Teeth of the Dog */}
            <div className="bg-gradient-to-br from-emerald-50 to-blue-50 rounded-2xl overflow-hidden shadow-xl">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="relative h-96 md:h-auto">
                  <img 
                    src="https://static.readdy.ai/image/57c982531ce74d8992a8785896e0bafd/0ca241a2434d94ad6a68309aecf1ad1d.jpeg" 
                    alt="Teeth of the Dog Golf Course" 
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-6 left-6">
                    <span className="bg-emerald-600 text-white px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider">
                      2 ROUNDS INCLUDED
                    </span>
                  </div>
                </div>
                <div className="p-12 flex flex-col justify-center">
                  <div className="mb-4">
                    <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Teeth of the Dog</h3>
                    <div className="flex items-center gap-2 text-emerald-600 mb-4">
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-fill"></i>
                      <span className="text-gray-600 ml-2">#1 Caribbean | #20 World</span>
                    </div>
                  </div>
                  <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                    Pete Dye's legendary oceanside masterpiece. Seven holes play directly along the Caribbean Sea. Ranked #1 in the Caribbean and #20 in the World Top 100 by Golf Digest. Pete Dye famously quirked: "I created 11 holes here, and God made the other 7."
                  </p>
                  <a href="https://casadecampoliving.com/teeth-of-the-dog-a-sneak-peek-at-the-exciting-renovations/" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 font-semibold text-lg inline-flex items-center gap-2 group">
                    View renovation details
                    <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                  </a>
                </div>
              </div>
            </div>

            {/* Dye Fore */}
            <div className="bg-gradient-to-br from-blue-50 to-emerald-50 rounded-2xl overflow-hidden shadow-xl">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="p-12 flex flex-col justify-center order-2 md:order-1">
                  <div className="mb-4">
                    <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Dye Fore</h3>
                    <div className="flex items-center gap-2 text-emerald-600 mb-4">
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-half-fill"></i>
                    </div>
                  </div>
                  <p className="text-gray-700 text-lg leading-relaxed">
                    Dramatic elevation changes and stunning views of the Chavón River and Caribbean Sea. Another Pete Dye design showcasing his genius for strategic, memorable golf.
                  </p>
                </div>
                <div className="relative h-96 md:h-auto order-1 md:order-2">
                  <img 
                    src="https://static.readdy.ai/image/57c982531ce74d8992a8785896e0bafd/b39338b1acb0a811b31a16e230e03629.jpeg" 
                    alt="Dye Fore Golf Course" 
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-6 right-6">
                    <span className="bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider">
                      1 ROUND INCLUDED
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* The Links */}
            <div className="bg-gradient-to-br from-amber-50 to-emerald-50 rounded-2xl overflow-hidden shadow-xl">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="relative h-96 md:h-auto">
                  <img 
                    src="https://static.readdy.ai/image/57c982531ce74d8992a8785896e0bafd/e47f5a581152a8366de14ccb807c9279.jpeg" 
                    alt="The Links Golf Course" 
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-6 left-6">
                    <span className="bg-amber-600 text-white px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider">
                      BONUS REPLAYS
                    </span>
                  </div>
                </div>
                <div className="p-12 flex flex-col justify-center">
                  <div className="mb-4">
                    <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">The Links</h3>
                    <div className="flex items-center gap-2 text-emerald-600 mb-4">
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-fill"></i>
                    </div>
                  </div>
                  <p className="text-gray-700 text-lg leading-relaxed">
                    For those who can't get enough, complimentary replay rounds are available on The Links or Dye Fore. Subject to tee time availability - book early!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Package Includes */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Everything You Need for the Perfect Golf Escape</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              All-Inclusive Package
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {/* Accommodations */}
            <div className="bg-gray-50 rounded-lg p-8">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-hotel-bed-line text-3xl text-emerald-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Accommodations</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-gray-700">
                  <i className="ri-check-line text-emerald-600 text-xl mt-0.5 flex-shrink-0"></i>
                  <span>4 nights in elite rooms</span>
                </li>
                <li className="flex items-start gap-3 text-gray-700">
                  <i className="ri-check-line text-emerald-600 text-xl mt-0.5 flex-shrink-0"></i>
                  <span>One transportation cart per room</span>
                </li>
                <li className="flex items-start gap-3 text-gray-700">
                  <i className="ri-check-line text-emerald-600 text-xl mt-0.5 flex-shrink-0"></i>
                  <span>Airport transfers included</span>
                </li>
              </ul>
            </div>

            {/* Dining */}
            <div className="bg-gray-50 rounded-lg p-8">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-restaurant-line text-3xl text-emerald-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Dining</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-gray-700">
                  <i className="ri-check-line text-emerald-600 text-xl mt-0.5 flex-shrink-0"></i>
                  <span>Daily breakfast, lunch, and dinner in gourmet restaurants</span>
                </li>
                <li className="flex items-start gap-3 text-gray-700">
                  <i className="ri-check-line text-emerald-600 text-xl mt-0.5 flex-shrink-0"></i>
                  <span>One special evening at the Beach Club</span>
                </li>
                <li className="flex items-start gap-3 text-gray-700">
                  <i className="ri-check-line text-emerald-600 text-xl mt-0.5 flex-shrink-0"></i>
                  <span>Unlimited drinks in all hotel bars, restaurants, and beverage carts</span>
                </li>
              </ul>
            </div>

            {/* Golf & Extras */}
            <div className="bg-gray-50 rounded-lg p-8">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-golf-ball-line text-3xl text-emerald-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Golf & Extras</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-gray-700">
                  <i className="ri-check-line text-emerald-600 text-xl mt-0.5 flex-shrink-0"></i>
                  <span>Two rounds on Teeth of the Dog</span>
                </li>
                <li className="flex items-start gap-3 text-gray-700">
                  <i className="ri-check-line text-emerald-600 text-xl mt-0.5 flex-shrink-0"></i>
                  <span>One round on Dye Fore</span>
                </li>
                <li className="flex items-start gap-3 text-gray-700">
                  <i className="ri-check-line text-emerald-600 text-xl mt-0.5 flex-shrink-0"></i>
                  <span>Complimentary replay rounds</span>
                </li>
                <li className="flex items-start gap-3 text-gray-700">
                  <i className="ri-check-line text-emerald-600 text-xl mt-0.5 flex-shrink-0"></i>
                  <span>Golf carts & unlimited range balls</span>
                </li>
                <li className="flex items-start gap-3 text-gray-700">
                  <i className="ri-check-line text-emerald-600 text-xl mt-0.5 flex-shrink-0"></i>
                  <span>Club storage</span>
                </li>
                <li className="flex items-start gap-3 text-gray-700">
                  <i className="ri-check-line text-emerald-600 text-xl mt-0.5 flex-shrink-0"></i>
                  <span>Tournament favors and awards</span>
                </li>
                <li className="flex items-start gap-3 text-gray-700">
                  <i className="ri-check-line text-emerald-600 text-xl mt-0.5 flex-shrink-0"></i>
                  <span>All taxes and service charges included</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Feature Images */}
          <div className="grid md:grid-cols-2 gap-8">
            <div className="relative h-96 rounded-lg overflow-hidden shadow-lg">
              <img 
                src="https://static.readdy.ai/image/57c982531ce74d8992a8785896e0bafd/47e97b9470cc130dce2a216b35152dfb.webp" 
                alt="Premium Dining Experience" 
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end">
                <div className="p-8">
                  <h3 className="text-3xl font-bold text-white mb-2">Premium Dining Experience</h3>
                  <p className="text-white/90 text-lg">Gourmet dining with Caribbean views</p>
                </div>
              </div>
            </div>

            <div className="relative h-96 rounded-lg overflow-hidden shadow-lg">
              <img 
                src="https://static.readdy.ai/image/57c982531ce74d8992a8785896e0bafd/c1b9829deaad0b1d1a9c0d3ec84d0f75.jpeg" 
                alt="Elite Resort Accommodations" 
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end">
                <div className="p-8">
                  <h3 className="text-3xl font-bold text-white mb-2">Elite Resort Accommodations</h3>
                  <p className="text-white/90 text-lg">Elite accommodations and hospitality</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="registration-section" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Final Payment</h2>
            <p className="text-xl text-red-600 font-semibold max-w-3xl mx-auto">
              REGISTERED PARTICIPANTS - Complete Your Final Payment
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            {/* Desktop Table View */}
            <div className="hidden md:block bg-white rounded-lg shadow-lg overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="py-6 px-8 text-left text-gray-600 font-semibold text-lg border-r border-gray-200"></th>
                    <th className="py-6 px-8 text-center">
                      <div className="text-2xl font-bold text-gray-900 mb-1">Single Occupancy</div>
                      <div className="text-sm text-gray-600">(1 person per room)</div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-gray-200">
                    <td className="py-6 px-8 font-semibold text-gray-900 text-lg border-r border-gray-200">Total Package</td>
                    <td className="py-6 px-8 text-center text-2xl font-bold text-gray-500 line-through">$3,959</td>
                  </tr>
                  <tr className="border-t border-gray-200 bg-gray-50">
                    <td className="py-6 px-8 font-semibold text-gray-900 text-lg border-r border-gray-200">Deposit Paid</td>
                    <td className="py-6 px-8 text-center text-2xl font-bold text-emerald-600">-$500</td>
                  </tr>
                  <tr className="border-t border-gray-200 bg-emerald-50">
                    <td className="py-6 px-8 font-semibold text-gray-900 text-lg border-r border-gray-200">Balance Due</td>
                    <td className="py-6 px-8 text-center text-3xl font-bold text-emerald-600">$3,459</td>
                  </tr>
                  <tr className="border-t border-gray-200 bg-gray-50">
                    <td className="py-6 px-8 font-semibold text-gray-900 text-lg border-r border-gray-200"></td>
                    <td className="py-6 px-8 text-center">
                      <button 
                        onClick={() => handleRegistration('single')}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 rounded-lg text-lg font-semibold transition-all transform hover:scale-105 shadow-lg whitespace-nowrap w-full cursor-pointer"
                      >
                        Pay Balance Now
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
              
              <div className="border-t border-gray-200 bg-gray-50 py-6 px-8">
                <div className="space-y-4">
                  <p className="text-gray-700 text-lg">
                    <strong>Payment Information:</strong>
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-emerald-600 mt-1 flex-shrink-0"></i>
                      <span>Your $500 deposit has been applied - Balance due: $3,459</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-emerald-600 mt-1 flex-shrink-0"></i>
                      <span>All payments are non-refundable</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-emerald-600 mt-1 flex-shrink-0"></i>
                      <span>If you need to cancel, your payment may be used as a credit for a future stay booked within 12 months</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-6">
              {/* Single Occupancy Card */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="bg-gray-100 p-4 text-center">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">Single Occupancy</h3>
                  <p className="text-sm text-gray-600">(1 person per room)</p>
                </div>
                <div className="p-4 space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="font-semibold text-gray-900">Total Package</span>
                    <span className="text-xl font-bold text-gray-500 line-through">$3,959</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="font-semibold text-gray-900">Deposit Paid</span>
                    <span className="text-xl font-bold text-emerald-600">-$500</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-200 bg-emerald-50 -mx-4 px-4">
                    <span className="font-bold text-gray-900">Balance Due</span>
                    <span className="text-2xl font-bold text-emerald-600">$3,459</span>
                  </div>
                  <button 
                    onClick={() => handleRegistration('single')}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-lg text-base font-semibold transition-all shadow-lg whitespace-nowrap cursor-pointer"
                  >
                    Pay Balance Now
                  </button>
                </div>
              </div>

              {/* Payment Info */}
              <div className="bg-white rounded-lg shadow-lg p-4">
                <div className="space-y-3">
                  <p className="text-gray-700 font-semibold">
                    Payment Information:
                  </p>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-emerald-600 mt-0.5 flex-shrink-0"></i>
                      <span>Your $500 deposit has been applied - Balance due: $3,459</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-emerald-600 mt-0.5 flex-shrink-0"></i>
                      <span>All payments are non-refundable</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-emerald-600 mt-0.5 flex-shrink-0"></i>
                      <span>If you need to cancel, your payment may be used as a credit for a future stay booked within 12 months</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Competition & Camaraderie */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Competition & Camaraderie</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              All skill levels welcome. This is about the experience, the courses, and the memories as much as the competition.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {/* Format */}
            <div className="bg-gradient-to-br from-emerald-50 to-blue-50 rounded-2xl p-10">
              <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mb-6">
                <i className="ri-trophy-line text-3xl text-white"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Format</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <p className="text-gray-700 text-lg">Three-day Stableford scoring system</p>
                </div>
              </div>
            </div>

            {/* Awards */}
            <div className="bg-gradient-to-br from-amber-50 to-emerald-50 rounded-2xl p-10">
              <div className="w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center mb-6">
                <i className="ri-medal-line text-3xl text-white"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Awards</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <p className="text-gray-700 text-lg">Daily gross winners</p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <p className="text-gray-700 text-lg">Daily net winners</p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <p className="text-gray-700 text-lg">Overall gross champion</p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <p className="text-gray-700 text-lg">Overall net champion</p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <p className="text-gray-700 text-lg">Tournament favors for all participants</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Once-in-a-Lifetime Opportunity */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">A Once-in-a-Lifetime Opportunity</h2>
          </div>

          <div className="max-w-4xl mx-auto space-y-8 text-lg text-gray-700 leading-relaxed">
            <p>
              Casa de Campo is one of the Caribbean's most exclusive resorts, and Teeth of the Dog is a bucket list course that every passionate golfer dreams of playing. With the recent renovation complete, this is the perfect time to experience Pete Dye's masterpiece in its finest form.
            </p>
            <p>
              This is an intimate, exclusive event with limited capacity. We're keeping the group small to ensure the best possible experience for every participant - from tee times to dining reservations to the overall tournament atmosphere.
            </p>
            <p className="text-xl font-semibold text-gray-900">
              This isn't just a golf trip—it's an escape to paradise with a select group of golfers who share your passion for the game and appreciation for legendary venues.
            </p>
          </div>

          {/* Feature Image */}
          <div className="mt-16 relative h-96 rounded-2xl overflow-hidden shadow-2xl">
            <img 
              src="https://static.readdy.ai/image/57c982531ce74d8992a8785896e0bafd/7072dc4b0739e0f36063f66feba57f4b.webp" 
              alt="Casa de Campo Paradise" 
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end">
              <div className="p-12 text-center w-full">
                <h3 className="text-4xl font-bold text-white mb-2">Casa de Campo Paradise</h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-2xl p-6 sm:p-8 md:p-12 shadow-2xl">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6">Secure Your Spot - Limited Availability</h2>
            <p className="text-lg sm:text-xl md:text-2xl text-white/90 mb-3 sm:mb-4 font-semibold">
              EXCLUSIVE EVENT - SPOTS ARE LIMITED
            </p>
            <p className="text-base sm:text-lg md:text-xl text-white/90 mb-6 sm:mb-8">
              This intimate event will fill quickly. Register now to guarantee your place in paradise.
            </p>
            <button 
              onClick={scrollToRegistration}
              className="bg-white hover:bg-gray-100 text-emerald-600 px-6 sm:px-8 md:px-10 py-3 sm:py-4 rounded-lg text-base sm:text-lg md:text-xl font-bold transition-all transform hover:scale-105 shadow-lg whitespace-normal sm:whitespace-nowrap cursor-pointer w-full sm:w-auto"
            >
              Register Now - Limited Spots Remaining
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-emerald-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold mb-6">Questions? Contact us below.</h3>
            <div className="space-y-4">
              <a 
                href="mailto:Info@BucketListGolf.org" 
                className="flex items-center justify-center gap-3 text-xl hover:text-white/80 transition-colors"
              >
                <i className="ri-mail-line text-2xl"></i>
                <span>Info@BucketListGolf.org</span>
              </a>
              <a 
                href="tel:4132227701" 
                className="flex items-center justify-center gap-3 text-xl hover:text-white/80 transition-colors"
              >
                <i className="ri-phone-line text-2xl"></i>
                <span>(413) 222-7701</span>
              </a>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8 text-center">
            <div className="mb-6">
              <h2 className="text-3xl font-bold">Bucket List Golf</h2>
            </div>
            <p className="text-white/80 text-sm">
              © 2026 Bucket List Golf. All rights reserved. | 
              <a href="https://readdy.ai/?origin=logo" className="hover:text-white ml-2">Website Builder</a>
            </p>
          </div>
        </div>
      </footer>

      <RegistrationModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        occupancy={modalOccupancy}
      />
    </div>
  );
}
